import React from "react";
import { defineMessages, FormattedMessage as T, useIntl } from "react-intl";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import Backdrop from "@material-ui/core/Backdrop";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Portal from "@material-ui/core/Portal";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import { ErrorOutline } from "@material-ui/icons";

import {
  getOcrReliabilityColor,
  Ticket,
  ControlReview,
  ControlAdditionalData,
  VehicleTypeRecord,
  TerminalType,
} from "@egis-smartcity/parking";
import { OcrProps } from "./Ocr";
import Actions from "./Actions";
import ControlForm, {
  ControlFormValues,
  emptyControlFormValues,
} from "./ControlForm";
import FullscreenNotification from "../FullscreenNotification";
import ImageControl, { ImageWithThumbnail } from "./ImageControl";
import LocationControl, {
  LocationControlFormValues,
  emptyLocationControlFormValues,
} from "./LocationControl";
import ProductivityAgentLeft from "./ProductivityAgent/ProductivityAgentLeft";
import ProductivityAgentRight from "./ProductivityAgent/ProductivityAgentRight";
import TicketPanel from "./TicketControl";
import useAuth from "../../hooks/useAuth";
import useCityConfig from "../../hooks/useCityConfig";
import useControlReview from "../../hooks/useControlReview";
import useLayoutPortal from "../../hooks/useLayoutPortal";
import useTicket from "../../hooks/useTicket";
import { LapiService } from "../../lib/lapi";
import globalState from "../globalState";
import Clock from "../Clock/Clock";
import { useInterpret, useSelector } from "@xstate/react";
import { timerMachine } from "../../core/timer";
import useCommonConfig from "../../hooks/useCommonConfig";
import { v4 } from "uuid";
import { sToDhms } from "../../utils";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  collapse: {
    marginLeft: "auto",
    marginRight: "auto",
  },
  imageReviewContainer: {
    flex: 1,
    minWidth: "min-content",
    display: "flex",
    flexDirection: "row",
    padding: theme.spacing(1),
    overflow: "hidden",
  },
  leftSection: {
    flex: 4,
    display: "flex",
  },
  imageControlContainer: {
    flex: 1,
    margin: theme.spacing(1),
  },
  rightSection: {
    flex: 2,
    display: "flex",
    flexDirection: "column",
  },
  controlFormContainer: {
    flex: 4,
    margin: theme.spacing(1),
    overflow: "hidden",
  },
  locationControlContainer: {
    flex: 3,
    margin: theme.spacing(1),
    overflow: "hidden",
  },
  ticketControlContainer: {
    flex: 2,
    margin: theme.spacing(1),
    overflow: "hidden",
  },
  actionsContainer: {
    margin: theme.spacing(1),
  },
  waitingMessageContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  waitingMessageActionsContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    "& button:last-child": {
      marginLeft: 16,
    },
  },
  bannerTimer: {
    fontWeight: 700,
  },
  separator: {
    height: "60%",
    borderLeft: "1px solid white",
    margin: `0 ${theme.spacing(1.5)}px`,
  },
  statsLabel: {
    display: "flex",
    alignItems: "center",
    "& >:first-child": {
      marginRight: 6,
      padding: 4,
      backgroundColor: "white",
      borderRadius: "50%",
    },
  },
  loadingBackdrop: {
    color: theme.palette.common.white,
    zIndex: theme.zIndex.appBar - 1,
  },
  portalContainer: {
    height: 40,
    display: "flex",
    alignItems: "center",
  },
}));

const msg = defineMessages({
  processingTimeNotif: {
    id: "imageReview.processingTimeNotif.message",
    description: "Message of the processing time exceeded notification.",
    defaultMessage:
      "Processing time exceeded. The control can no longer be processed.",
  },
  validityTimeNotif: {
    id: "imageReview.validityTimeNotif.message",
    description: "Message of the validity time expired notification.",
    defaultMessage:
      "Validity time expired. The control can no longer be processed.",
  },
  waitingMessageLabel: {
    id: "imageReview.waitingMessage.label",
    description: "Message for waiting a control notification.",
    defaultMessage: "No controls are currently available.",
  },
  notFoundMessageLabel: {
    id: "imageReview.notFoundMessage.label",
    description: "Message when control cannot be processed anymore.",
    defaultMessage: "This control can no longer be processed.",
  },
  waitingMessageLogoutButtonLabel: {
    id: "imageReview.waitingMessage.logoutButton.label",
    description: "Label of the logout button of the waiting message.",
    defaultMessage: "Log out",
  },
  waitingMessageRefreshButtonLabel: {
    id: "imageReview.waitingMessage.refreshButton.label",
    description: "Label of the refresh button of the waiting message.",
    defaultMessage: "Refresh",
  },
  bannerMessage: {
    id: "imageReview.banner.message",
    defaultMessage:
      "Vous avez atteint la limite de 999 FPS émis pour la même date de constatation. Nouveau contrôle disponible dans ",
  },
});

function ImageReview() {
  const classes = useStyles();
  const { formatMessage: t } = useIntl();
  const { cityId, logout, createAccountUrl } = useAuth();
  const { cityConfig } = useCityConfig();
  const { controlReviewService } = useControlReview();

  const bannerText =
    "Vous avez atteint la limite de 999 FPS émis pour la même date de constatation.";

  const { ticketService } = useTicket();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only("xs"));
  const sm = useMediaQuery(theme.breakpoints.only("sm"));
  const { carBrands, carModels, countries, vehicleTypes } = useCommonConfig();

  const [initialControlReviewFetched, setInitialControlReviewFetched] =
    React.useState(false);
  const [controlAvailable, setControlAvailable] = React.useState(true);
  const [controlNotFound, setControlNotFound] = React.useState(false);
  const [controlReview, setControlReview] = React.useState<ControlReview>();
  const [tickets, setTickets] = React.useState<Ticket[]>(); // Ticket(s) associated with the control
  const {
    leftContainer,
    rightContainer,
    bannerContainer,
    isFpsLimitExceeded,
    setIsFpsLimitExceeded,
  } = useLayoutPortal();

  // @ts-ignore
  const isControl999 = controlReview?.isFpsLimitExceeded === true;

  React.useEffect(() => {
    setIsFpsLimitExceeded(isControl999);
  }, [isControl999, setIsFpsLimitExceeded]);
  const [ticketsValidity, setTicketsValidity] = React.useState<boolean>(); // Ticket validity
  const [selectedTicket, setSelectedTicket] = React.useState<Ticket>();
  const [imageSelection, setImageSelection] = React.useState<string[]>([]);
  const [controlFormValues, setControlFormValues] =
    React.useState<ControlFormValues>(emptyControlFormValues);
  const [initialControlFormValues, setInitialControlFormValues] =
    React.useState<ControlFormValues>(emptyControlFormValues);
  const [locationControlFormValues, setLocationControlFormValues] =
    React.useState<LocationControlFormValues>(emptyLocationControlFormValues);
  const [
    initialLocationControlFormValues,
    setInitialLocationControlFormValues,
  ] = React.useState<LocationControlFormValues>(emptyLocationControlFormValues);
  const [loading, setLoading] = React.useState(false);
  const [isSivDataAvailable, setIsSivDataAvailable] = React.useState<boolean>();
  const [launchValidation, setLaunchValidation] =
    React.useState<boolean>(false);
  const [showValidationErrors, setShowValidationErrors] =
    React.useState<boolean>(false);
  const processingTimerService = useInterpret(timerMachine);
  const processingTimerExpired = useSelector(processingTimerService, (state) =>
    state.matches("expired")
  );
  const validityTimerService = useInterpret(timerMachine);

  const processingTimerState = useSelector(
    processingTimerService,
    (state) => state
  );

  const remainingProcessingTime = React.useMemo(() => {
    if (processingTimerState.matches("new")) return null;

    const remaining =
      (processingTimerState.context.duration as number) -
      (processingTimerState.context.elapsed as number);

    return sToDhms(remaining);
  }, [processingTimerState]);

  const [notifMessage, setNotifMessage] = React.useState("");

  const reliabilityRate = React.useMemo(
    () =>
      controlReview && controlReview.control
        ? Math.round(controlReview.control.gen_reliability * 100)
        : undefined,
    [controlReview]
  );

  const reliabilityColor = React.useMemo(
    () =>
      getOcrReliabilityColor(
        reliabilityRate,
        cityConfig?.review.lapi.ocrReliability.paint["fill-color"]
      ),
    [cityConfig, reliabilityRate]
  );

  const agentFullName = React.useMemo(() => {
    if (!cityConfig?.agent?.givenName) return;
    if (!cityConfig?.agent?.familyName) return;

    return `${cityConfig.agent.givenName} ${cityConfig.agent.familyName}`;
  }, [cityConfig]);

  // Reset all of the ImageReview state.
  const resetImageReview = React.useCallback(() => {
    processingTimerService.send("RESET");
    validityTimerService.send("RESET");
    // processingTimerService.stop();
    // validityTimerService.stop();
    setControlAvailable(true);
    setControlNotFound(false);
    setControlFormValues(emptyControlFormValues);
    setInitialControlFormValues(emptyControlFormValues);
    setControlReview(undefined);
    // setDelay(null);
    setImageSelection([]);
    setLoading(false);
    setLocationControlFormValues(emptyLocationControlFormValues);
    setNotifMessage("");
    // setProcessingTimer(undefined);
    setTickets(undefined);
    setTicketsValidity(undefined);
    setSelectedTicket(undefined);
    // setTimeExceeded(false);
    // setValidityTimer(undefined);
    LapiService.clearCache();
    globalState.clear();
  }, [processingTimerService, validityTimerService]);

  const refreshTickets = React.useCallback(
    (
      licensePlate: string,
      params?: {
        accuracy?: number;
        latitude?: number;
        longitude?: number;
        scandate?: string;
        terminalType?: TerminalType;
        matricule?: string;
        requestSrc?: string;
      }
    ) => {
      if (!ticketService || !cityId || licensePlate === "") return;

      // Reset the tickets metadata.
      setTickets(undefined);
      setTicketsValidity(undefined);
      setSelectedTicket(undefined);

      ticketService
        .getTicketControlByLicensePlate(cityId, licensePlate, {
          terminalType: "IMAGE_REVIEW",
          matricule: cityConfig?.agent?.agentId,
          ...params,
        })
        .then((ticketControl) => {
          setTicketsValidity(ticketControl.status === "VALID");
          if (ticketControl.results === undefined) return;
          setTickets(ticketControl.results);
          setSelectedTicket(ticketControl.results[0]);
        })
        .catch((error) => {
          if (process.env.NODE_ENV !== "production") console.error(error);
        });
    },
    [cityConfig, cityId, ticketService]
  );

  // Here we will fetch the initial controlReview.
  React.useEffect(() => {
    let mounted = true;

    if (!controlReviewService || initialControlReviewFetched) return;

    setLoading(true);

    controlReviewService
      .getControlReview()
      .then((controlReview) => {
        if (mounted) {
          if (!controlReview) {
            setControlAvailable(false);
          } else {
            setControlReview(controlReview);
          }
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
        if (mounted) {
          if (error.response?.status === 404) {
            setControlNotFound(true);
          } else {
            setControlAvailable(false);
          }
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
          setInitialControlReviewFetched(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [controlReviewService, initialControlReviewFetched]);

  // Here we will feed the control form based on the LAPI control contained
  // in the controlReview.
  React.useEffect(() => {
    if (!controlReview || !controlReview.control) return;

    let carBrand: ControlFormValues["carBrand"] = {
      id: "",
      label: "",
      value: "",
    };

    let carModel: ControlFormValues["carModel"] = null;

    let vehicle_category: ControlFormValues["vehicleType"] = {
      label: "",
      value: "",
    };

    const researched_country = countries?.find((current_country) => {
      return (
        current_country.value.toLocaleLowerCase() ===
        controlReview?.control?.plate_country?.toLocaleLowerCase()
      );
    });

    if (cityConfig?.review?.siv) {
      const researched_brand = carBrands?.find((current_brand) => {
        return (
          current_brand.value.toLocaleLowerCase() ===
          controlReview?.control?.vehicle_brand?.toLocaleLowerCase()
        );
      });

      carBrand = {
        id: controlReview?.additionalData?.brand?.id
          ? controlReview?.additionalData?.brand?.id
          : researched_brand?.id
          ? researched_brand?.id || ""
          : controlReview?.control?.vehicle_brand
          ? v4()
          : "",
        label: controlReview?.additionalData?.brand?.label
          ? controlReview?.additionalData?.brand?.label
          : researched_brand?.label
          ? researched_brand?.label || ""
          : controlReview?.control?.vehicle_brand
          ? controlReview?.control?.vehicle_brand!
          : "",
        value: controlReview?.additionalData?.brand?.value
          ? controlReview?.additionalData?.brand?.value
          : researched_brand?.value
          ? researched_brand?.value || ""
          : controlReview?.control?.vehicle_brand
          ? controlReview?.control?.vehicle_brand!
          : "",
      };

      const researched_model = carModels?.find((current_model) => {
        return (
          current_model.value.toLocaleLowerCase() ===
          controlReview?.control?.vehicle_model?.toLocaleLowerCase()
        );
      });

      if (
        !controlReview?.control?.vehicle_model ||
        controlReview?.control?.vehicle_model === "IGNOREE" ||
        controlReview?.control?.vehicle_brand === "IGNOREE"
      ) {
        carModel = null;
      } else {
        carModel = {
          id: controlReview?.additionalData?.model?.id
            ? controlReview?.additionalData?.model?.id
            : researched_model?.id
            ? researched_model?.id
            : v4(),
          label: controlReview?.additionalData?.model?.label
            ? controlReview?.additionalData?.model?.label
            : researched_model?.label
            ? researched_model?.label
            : controlReview?.control?.vehicle_model,
          value: controlReview?.additionalData?.model?.value
            ? controlReview?.additionalData?.model?.value
            : researched_model?.value
            ? researched_model?.value
            : controlReview?.control?.vehicle_model,
          carBrand,
        };
      }

      const researched_vehicle_category =
        cityConfig?.review.fps.vehicleType.find((current_vehicle_category) => {
          return (
            current_vehicle_category.value?.toLocaleLowerCase() ===
            controlReview?.control?.vehicle_category?.toLocaleLowerCase()
          );
        });
      vehicle_category = {
        label:
          controlReview?.additionalData?.vehicleType?.label ||
          researched_vehicle_category?.label ||
          "",
        value:
          controlReview?.additionalData?.vehicleType?.value ||
          researched_vehicle_category?.value ||
          "",
      };

      setIsSivDataAvailable(
        Boolean(
          controlReview?.control?.vehicle_brand &&
            controlReview?.control?.vehicle_brand !== "IGNOREE" &&
            controlReview?.control?.vehicle_category
        )
      );
    }

    setControlFormValues({
      licensePlate:
        controlReview.additionalData?.plate ||
        controlReview.control.plate ||
        "",
      vehicleType: {
        label:
          vehicle_category.label === ""
            ? cityConfig?.review?.fps?.vehicleType[0]?.label || ""
            : vehicle_category.label,
        value:
          vehicle_category.value === ""
            ? cityConfig?.review?.fps?.vehicleType[0]?.value || ""
            : vehicle_category.value,
      },
      carBrand,
      carModel,
      country: {
        id:
          controlReview?.additionalData?.country?.id ||
          researched_country?.id ||
          "c7803ad8c268317793f459b11d028b25",
        label:
          controlReview?.additionalData?.country?.label ||
          researched_country?.label ||
          "France (la)",
        value:
          controlReview?.additionalData?.country?.value ||
          researched_country?.value ||
          "FR",
      },
      dateTime: controlReview.control.date || "",
    });

    setInitialControlFormValues({
      licensePlate:
        controlReview.additionalData?.plate ||
        controlReview.control.plate ||
        "",
      vehicleType: {
        label:
          vehicle_category.label === ""
            ? cityConfig?.review?.fps?.vehicleType[0]?.label || ""
            : vehicle_category.label,
        value:
          vehicle_category.value === ""
            ? cityConfig?.review?.fps?.vehicleType[0]?.value || ""
            : vehicle_category.value,
      },
      carBrand,
      carModel,
      country: {
        id:
          controlReview?.additionalData?.country?.id ||
          researched_country?.id ||
          "c7803ad8c268317793f459b11d028b25",
        label:
          controlReview?.additionalData?.country?.label ||
          researched_country?.label ||
          "France (la)",
        value:
          controlReview?.additionalData?.country?.value ||
          researched_country?.value ||
          "FR",
      },
      dateTime: controlReview.control.date || "",
    });
  }, [
    cityConfig,
    controlReview,
    carBrands,
    carModels,
    countries,
    vehicleTypes,
  ]);

  React.useEffect(() => {
    if (controlReview?.additionalData?.mediasUrl === undefined) return;

    setImageSelection(controlReview.additionalData.mediasUrl);
  }, [controlReview]);

  // Here we will feed the location control form based on the LAPI control
  // location contained in the controlReview with a geocoder.
  React.useEffect(() => {
    if (
      !controlReview ||
      typeof controlReview.control?.gps_latitude !== "number" ||
      typeof controlReview.control?.gps_longitude !== "number"
    )
      return;

    let nextLocationControlFormValues: LocationControlFormValues = {
      lat:
        controlReview.additionalData?.position?.lat ||
        controlReview.control.gps_latitude,
      lon:
        controlReview.additionalData?.position?.lon ||
        controlReview.control.gps_longitude,
      zone: {
        label: controlReview.additionalData?.zone?.label || "",
        value: controlReview.additionalData?.zone?.value || "",
      },
      sector: {
        label: controlReview.additionalData?.sector?.label || "",
        value: controlReview.additionalData?.sector?.value || "",
      },
      streetNumber: controlReview.additionalData?.streetNumber || "",
      address: controlReview.additionalData?.address || "",
      postalCode: controlReview.additionalData?.postalCode || "",
      city: controlReview.additionalData?.city || "",
    };

    setLocationControlFormValues(nextLocationControlFormValues);
    setInitialLocationControlFormValues(nextLocationControlFormValues);
  }, [cityConfig, controlReview]);

  const getControlReview = React.useCallback(
    (force?: number) => {
      if (!controlReviewService) return;

      resetImageReview();

      setLoading(true);

      controlReviewService
        .getControlReview(force)
        .then((controlReview) => {
          // eslint-disable-next-line
          if (!controlReview) {
            setControlAvailable(false);
          } else {
            setControlReview(controlReview);
          }
        })
        .catch((error) => {
          if (process.env.NODE_ENV !== "production") console.error(error);
          if (error.response?.status === 404) {
            setControlNotFound(true);
          } else {
            setControlAvailable(false);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [controlReviewService, resetImageReview]
  );

  React.useEffect(() => {
    if (!controlReview || !cityConfig) return;

    const validityTimerStart = new Date(
      controlReview.control?.date as string
    ).getTime();
    const validityTimerEnd = new Date(
      controlReview.expiresAt as string
    ).getTime();
    validityTimerService.send({
      type: "INIT",
      start: validityTimerStart,
      end: validityTimerEnd,
      interval: 1,
    });
    validityTimerService.send("START");

    const processingTimerStart = new Date(
      controlReview?.assignedAt as string
    ).getTime();
    const processingTimerEnd =
      processingTimerStart +
      cityConfig.review.controlReview.processingDelay.duration * 1000;
    processingTimerService.send({
      type: "INIT",
      start: processingTimerStart,
      end: processingTimerEnd,
      interval: 1,
    });
    processingTimerService.send("START");
  }, [cityConfig, controlReview, processingTimerService, validityTimerService]);

  React.useEffect(() => {
    if (!processingTimerExpired) return;

    validityTimerService.send("PAUSE");

    setNotifMessage(t(msg.processingTimeNotif));
    const timeoutId = setTimeout(() => {
      getControlReview();
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [getControlReview, processingTimerExpired, t, validityTimerService]);

  // Update the tickets metadata when the control review is ready.
  React.useEffect(() => {
    if (!controlReview || !controlReview.control) return;

    refreshTickets(
      controlReview.additionalData?.plate || controlReview.control.plate,
      {
        latitude:
          controlReview.additionalData?.position?.lat ||
          controlReview.control.gps_latitude,
        longitude:
          controlReview.additionalData?.position?.lon ||
          controlReview.control.gps_longitude,
        scandate: controlReview.control.date,
        matricule: "LAPI_REVIEW",
        requestSrc: cityConfig?.review.ticketsRequestSource
          ? "ImageReview"
          : undefined,
      }
    );
  }, [controlReview, refreshTickets, cityConfig?.review.ticketsRequestSource]);

  const handleControlFormSubmit = React.useCallback(
    (data: ControlFormValues, _event?: React.BaseSyntheticEvent) => {
      setControlFormValues(data);
    },
    []
  );

  const handleLocationControlFormDataChange = React.useCallback(
    (value: LocationControlFormValues) => {
      setLocationControlFormValues((prev) => {
        return {
          ...prev,
          ...value,
          sector: { ...prev.sector, ...value.sector },
          zone: { ...prev.zone, ...value.zone },
        };
      });
    },
    []
  );

  // Save the controlReview when some of its data has changed,
  // ATM some values are updated only when their associated form is valid.
  React.useEffect(() => {
    if (!controlReviewService || !controlReview) return;

    const controlAdditionalData: ControlAdditionalData = {
      plate: controlFormValues.licensePlate,
      country: { ...controlFormValues.country },
      vehicleType: { ...controlFormValues.vehicleType } as VehicleTypeRecord,
      brand:
        controlFormValues.carBrand === null
          ? undefined
          : {
              id: controlFormValues.carBrand.id,
              label: controlFormValues.carBrand.label,
              value: controlFormValues.carBrand.value,
            },
      model:
        controlFormValues.carModel === null
          ? undefined
          : {
              id: controlFormValues.carModel.id,
              label: controlFormValues.carModel.label,
              value: controlFormValues.carModel.value,
            },
      mediasUrl: [...imageSelection],
      zone: { ...locationControlFormValues.zone },
      sector: { ...locationControlFormValues.sector },
      streetNumber: locationControlFormValues.streetNumber,
      address: locationControlFormValues.address,
      postalCode: locationControlFormValues.postalCode,
      city: locationControlFormValues.city,
    };

    if (
      locationControlFormValues.lat !== null &&
      locationControlFormValues.lon !== null
    ) {
      controlAdditionalData.position = {
        lat: locationControlFormValues.lat,
        lon: locationControlFormValues.lon,
      };
    }

    // Debounce, will help with the ImageReview component init chaos.
    const timeoutId = setTimeout(() => {
      if (!controlReview.controlId) return;

      controlReviewService
        .saveControlReview(controlReview.controlId, controlAdditionalData)
        .catch((error) => {
          if (process.env.NODE_ENV !== "production") console.error(error);
        });
    }, 250);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    controlFormValues?.carBrand,
    controlFormValues?.carModel,
    controlFormValues?.country,
    controlFormValues?.licensePlate,
    controlFormValues?.vehicleType,
    controlReview,
    controlReviewService,
    imageSelection,
    locationControlFormValues?.streetNumber,
    locationControlFormValues?.address,
    locationControlFormValues?.city,
    locationControlFormValues?.lat,
    locationControlFormValues?.lon,
    locationControlFormValues?.postalCode,
    locationControlFormValues?.sector,
    locationControlFormValues?.zone,
  ]);

  const validationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleFormIsNotValid = React.useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    setShowValidationErrors(true);
    setLaunchValidation(true);

    validationTimeoutRef.current = setTimeout(() => {
      setLaunchValidation(false);
      validationTimeoutRef.current = null;
    }, 100);
  }, []);

  // Cleanup au démontage du composant
  React.useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const handleImageSelectionChange = React.useCallback(
    (selectedImages?: ImageWithThumbnail[]) => {
      setImageSelection(selectedImages?.map((img) => img.image) || []);
    },
    []
  );

  const handleResetImageSelection = React.useCallback(() => {
    setImageSelection([]);
  }, []);

  const handleLogoutButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      logout("hard");
    },
    [logout]
  );

  const handleCloseNotif = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>) => {
      getControlReview();
    },
    [getControlReview]
  );

  const handleRefreshTicketsButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      refreshTickets(controlFormValues.licensePlate, {
        latitude: locationControlFormValues.lat ?? undefined,
        longitude: locationControlFormValues.lon ?? undefined,
        scandate: controlReview?.control?.date,
        requestSrc: cityConfig?.review.ticketsRequestSource
          ? "ImageReview"
          : undefined,
      });
    },
    [
      controlFormValues?.licensePlate,
      controlReview,
      locationControlFormValues?.lat,
      locationControlFormValues?.lon,
      refreshTickets,
      cityConfig?.review.ticketsRequestSource,
    ]
  );

  const handleLicensePlateBlur = React.useCallback(
    (licensePlate: string) => {
      refreshTickets(licensePlate, {
        latitude: locationControlFormValues.lat ?? undefined,
        longitude: locationControlFormValues.lon ?? undefined,
        scandate: controlReview?.control?.date,
        requestSrc: cityConfig?.review.ticketsRequestSource
          ? "ImageReview"
          : undefined,
      });
    },
    [
      controlReview,
      locationControlFormValues?.lat,
      locationControlFormValues?.lon,
      refreshTickets,
      cityConfig?.review.ticketsRequestSource,
    ]
  );

  const handleRefresh = React.useCallback(
    (_event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      getControlReview();
    },
    [getControlReview]
  );

  const ocrProps: OcrProps = React.useMemo(
    () => ({
      plateImage: controlReview?.control?.plate_image,
      reliabilityColor: reliabilityColor,
      reliabilityRate: reliabilityRate,
      size: xs ? "small" : sm ? "medium" : "large",
      vehicleId: controlReview?.control?.vehicle_id,
    }),
    [controlReview, reliabilityColor, reliabilityRate, sm, xs]
  );

  const images = React.useMemo(() => {
    if (controlReview?.control?.pano_image === undefined) return;

    return controlReview.control.pano_image
      .map((img) => {
        if (!img.image) return null;

        return {
          image: img.image,
          thumbnail: img.thumbnail || img.image,
        };
      })
      .filter((item) => item !== null) as ImageWithThumbnail[];
  }, [controlReview]);

  React.useEffect(() => {
    globalState.licensePlate = controlFormValues?.licensePlate;
    globalState.scanDate = controlReview?.control?.date;
    globalState.lat = locationControlFormValues?.lat;
    globalState.lon = locationControlFormValues?.lon;
  }, [
    controlFormValues?.licensePlate,
    controlReview,
    locationControlFormValues?.lat,
    locationControlFormValues?.lon,
  ]);

  const productivityEnabled: boolean | undefined = React.useMemo(() => {
    //@ts-ignore
    return cityConfig?.review?.productivity?.enabled;
  }, [cityConfig]);

  return (
    <div className={classes.root}>
      <div className={classes.imageReviewContainer}>
        <div className={classes.leftSection}>
          <Paper className={classes.imageControlContainer}>
            <ImageControl
              images={images}
              selectionLimit={
                cityConfig?.review?.fullPictures
                  ? cityConfig?.review?.fps?.maxPicturesNumber || undefined
                  : cityConfig?.review?.fps?.picturesNumber
              }
              selectionOverride={controlReview?.additionalData?.mediasUrl}
              date={controlReview?.control?.date}
              valid={ticketsValidity}
              invalidColor={
                cityConfig?.review?.ticket?.validity?.paint["fill-color"]
                  ?.invalid
              }
              onImageSelectionChange={handleImageSelectionChange}
              onReset={handleResetImageSelection}
            />
          </Paper>
        </div>
        <div className={classes.rightSection}>
          <Paper className={classes.controlFormContainer}>
            <ControlForm
              values={controlFormValues}
              initialValues={initialControlFormValues}
              triggerControlFormValidation={launchValidation}
              showValidationErrors={showValidationErrors}
              onLicensePlateBlur={handleLicensePlateBlur}
              onSubmit={handleControlFormSubmit}
              onValuesChange={setControlFormValues}
              OcrProps={ocrProps}
              isSivDataAvailable={isSivDataAvailable}
            />
          </Paper>
          <Paper className={classes.locationControlContainer}>
            <LocationControl
              lapiControl={controlReview?.control}
              onFormDataChange={handleLocationControlFormDataChange}
              onMapMatchingProcess2={refreshTickets}
              ticket={selectedTicket}
              triggerLocationControlFormValidation={launchValidation}
              showValidationErrors={showValidationErrors}
              values={initialLocationControlFormValues}
            />
          </Paper>
          <Paper className={classes.ticketControlContainer}>
            <TicketPanel
              tickets={tickets}
              controlDate={controlReview?.control?.date}
              valid={ticketsValidity}
              invalidColor={
                cityConfig?.review?.ticket?.validity?.paint["fill-color"]
                  ?.invalid
              }
              onRefreshButtonClick={handleRefreshTicketsButtonClick}
            />
          </Paper>
          <div className={classes.actionsContainer}>
            <Actions
              controlReview={controlReview}
              controlFormValues={controlFormValues}
              locationControlFormValues={locationControlFormValues}
              tickets={tickets}
              ticketsValidity={ticketsValidity || false}
              licensePlateImageUrl={controlReview?.control?.plate_image}
              panoramicImagesUrls={imageSelection}
              onActionDone={getControlReview}
              onValidationFailure={handleFormIsNotValid}
            />
          </div>
        </div>
      </div>
      <FullscreenNotification
        open={!controlReview && (!controlAvailable || controlNotFound)}
      >
        <div className={classes.waitingMessageContainer}>
          <Typography gutterBottom>
            {!controlAvailable ? (
              <T {...msg.waitingMessageLabel} />
            ) : (
              <T {...msg.notFoundMessageLabel} />
            )}
          </Typography>
          <div className={classes.waitingMessageActionsContainer}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PowerSettingsNewIcon />}
              disabled={loading}
              onClick={handleLogoutButtonClick}
            >
              <T {...msg.waitingMessageLogoutButtonLabel} />
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={
                loading ? <CircularProgress size={20} /> : <RefreshIcon />
              }
              disabled={loading}
              onClick={handleRefresh}
            >
              <T {...msg.waitingMessageRefreshButtonLabel} />
            </Button>
          </div>
        </div>
      </FullscreenNotification>
      <Backdrop className={classes.loadingBackdrop} open={loading}>
        <CircularProgress color="inherit" size={64} />
      </Backdrop>
      <FullscreenNotification
        open={processingTimerExpired}
        type="warning"
        onCloseButtonClick={handleCloseNotif}
      >
        {notifMessage}
      </FullscreenNotification>
      <Portal container={leftContainer.current}>
        {productivityEnabled && (
          <ProductivityAgentLeft
            processingTimerService={processingTimerService}
            validityTimerService={validityTimerService}
            controlValidityColorCriteria={
              cityConfig?.review.lapi.validityDelay.paint["fill-color"]
            }
          />
        )}
      </Portal>
      <Portal container={rightContainer.current}>
        <div className={classes.portalContainer}>
          {productivityEnabled && (
            <ProductivityAgentRight controlReview={controlReview} />
          )}
          <Clock />
          <div className={classes.separator} />
          <Typography
            {...(createAccountUrl
              ? {
                  onClick: () => window.open(createAccountUrl(), "_blank"),
                  style: { cursor: "pointer" },
                }
              : {})}
          >
            {agentFullName}
          </Typography>
        </div>
      </Portal>
      <Portal container={bannerContainer?.current}>
        {isFpsLimitExceeded && productivityEnabled && remainingProcessingTime && (
          <div
            style={{
              width: "100%",
              color: "#000",
              padding: "10px 20px",
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              gap: 8,
              zIndex: theme.zIndex.appBar - 1,
            }}
          >
            <ErrorOutline style={{ fontSize: 32, color: "#000" }} />
            <span>
              {bannerText} Nouveau contrôle disponible dans{" "}
              <b>{remainingProcessingTime}</b>
            </span>
          </div>
        )}
      </Portal>
    </div>
  );
}

export default ImageReview;
